import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #onQuantityChangeBound = this.#onQuantityChange.bind(this);
  #onClickBound = this.#onClick.bind(this);
  #debouncedUpdateQuantity = debounce(this.updateQuantity, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClickBound);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClickBound);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeBound);
  }

  /**
   * Handles cart component clicks.
   * @param {MouseEvent} event - The click event.
   */
  #onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const clearButton = target.closest('[data-cart-clear-all]');
    if (clearButton instanceof HTMLButtonElement && this.contains(clearButton)) {
      event.preventDefault();
      this.clearCart(clearButton);
      return;
    }

    const removeLink = target.closest('.mc-item-remove, .cart-row-remove, .fc-action-remove');
    if (removeLink instanceof HTMLAnchorElement && this.contains(removeLink)) {
      event.preventDefault();
      const allKeys = removeLink.getAttribute('data-all-keys');
      if (allKeys && allKeys.includes(',')) {
        const keys = allKeys.split(',');
        const updates = {};
        keys.forEach((k) => { updates[k] = 0; });
        this.updateQuantityByUpdates(updates, 'clear');
        return;
      }
      try {
        const url = new URL(removeLink.href, window.location.origin);
        const line = parseInt(url.searchParams.get('line'));
        if (line) {
          this.onLineItemRemove(line);
        }
      } catch (err) {
        console.error('Failed to parse remove link line index:', err);
      }
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#debouncedUpdateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = (this.refs.cartItemRows || [])[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    if (textComponent && typeof textComponent.shimmer === 'function') {
      textComponent.shimmer();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRows = this.refs.cartItemRows || [];
    const cartItemRowToRemove = cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends or via fallback timeout
      let removed = false;
      const safeRemove = () => {
        if (removed) return;
        removed = true;
        remove();
      };
      onAnimationEnd(row, safeRemove);
      setTimeout(safeRemove, 1000); // 1-second fallback timeout
    });
  }

  /**
   * Updates quantities by a custom updates map.
   * @param {Object<string, number>} updates - Map of item keys to quantities.
   * @param {string} action - The user action name.
   */
  updateQuantityByUpdates(updates, action = 'clear') {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    const cartBaseUrl = Theme.routes.cart_url.replace(/\/$/, '');
    const fetchUrl = `${cartBaseUrl}/update.js?sections=${sections}`;
    const body = JSON.stringify({
      updates: updates,
      sections: sections,
      sections_url: window.location.pathname,
    });

    if (cartTotal && typeof cartTotal.shimmer === 'function') {
      cartTotal.shimmer();
    }

    fetch(fetchUrl, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('[Cart updates error]', parsedResponseText.errors);
          return;
        }

        let sectionHtml = parsedResponseText.sections ? parsedResponseText.sections[this.sectionId] : null;

        if (!sectionHtml && parsedResponseText.sections) {
          const normalizedId = this.sectionId.replace(/^sections--\d+__/, '');
          if (parsedResponseText.sections[normalizedId]) {
            sectionHtml = parsedResponseText.sections[normalizedId];
          } else {
            const keys = Object.keys(parsedResponseText.sections);
            const matchingKey = keys.find((key) => key.toLowerCase().includes('cart'));
            if (matchingKey) {
              sectionHtml = parsedResponseText.sections[matchingKey];
            } else if (keys.length === 1) {
              sectionHtml = parsedResponseText.sections[keys[0]];
            }
          }
        }

        if (!sectionHtml) {
          window.location.reload();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemRows = this.refs.cartItemRows || [];
    const lineItemRow = cartItemRows[line - 1];
    const allKeys = lineItemRow ? (lineItemRow.dataset.allKeys || lineItemRow.getAttribute('data-all-keys')) : null;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');

    let fetchUrl, body;
    if (allKeys && allKeys.includes(',')) {
      const keys = allKeys.split(',');
      const updates = {};
      keys.forEach((k, idx) => {
        updates[k] = (idx === 0) ? quantity : 0;
      });
      const cartBaseUrl = Theme.routes.cart_url.replace(/\/$/, '');
      fetchUrl = `${cartBaseUrl}/update.js?sections=${sections}`;
      body = JSON.stringify({
        updates: updates,
        sections: sections,
        sections_url: window.location.pathname,
      });
    } else {
      fetchUrl = `${Theme.routes.cart_change_url}?sections=${sections}`;
      body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: sections,
        sections_url: window.location.pathname,
      });
    }

    if (cartTotal && typeof cartTotal.shimmer === 'function') {
      cartTotal.shimmer();
    }

    fetch(fetchUrl, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        let sectionHtml = parsedResponseText.sections ? parsedResponseText.sections[this.sectionId] : null;

        if (!sectionHtml && parsedResponseText.sections) {
          // Fallback 1: search for normalized section ID (e.g. "header" instead of "sections--...__header")
          const normalizedId = this.sectionId.replace(/^sections--\d+__/, '');
          if (parsedResponseText.sections[normalizedId]) {
            sectionHtml = parsedResponseText.sections[normalizedId];
          } else {
            // Fallback 2: fuzzy match key names (e.g. "main-cart" matching "cart-section")
            const keys = Object.keys(parsedResponseText.sections);
            const matchingKey = keys.find(key => {
              const cleanKey = key.toLowerCase();
              const cleanId = normalizedId.toLowerCase();
              return cleanKey.includes(cleanId) || cleanId.includes(cleanKey) ||
                     (cleanKey.includes('cart') && cleanId.includes('cart')) ||
                     (cleanKey.includes('header') && cleanId.includes('header'));
            });
            if (matchingKey) {
              sectionHtml = parsedResponseText.sections[matchingKey];
            } else if (keys.length === 1) {
              // Fallback 3: if only one key is returned, use it
              sectionHtml = parsedResponseText.sections[keys[0]];
            }
          }
        }

        if (!sectionHtml) {
          console.error(`Section ${this.sectionId} not found in the response`, parsedResponseText);
          window.location.reload();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          sectionHtml,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Clears all cart items.
   * @param {HTMLButtonElement} clearButton - The clicked clear cart button.
   */
  clearCart(clearButton) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();
    clearButton.disabled = true;
    clearButton.setAttribute('aria-busy', 'true');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    const cartBaseUrl = Theme.routes.cart_url.replace(/\/$/, '');
    const clearUrl = `${cartBaseUrl}/clear.js?sections=${sections}`;
    const body = JSON.stringify({
      sections,
      sections_url: window.location.pathname,
    });

    fetch(clearUrl, fetchConfig('json', { body }))
      .then((response) => {
        if (!response.ok) throw new Error('Could not clear cart.');
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        let sectionHtml = parsedResponseText.sections ? parsedResponseText.sections[this.sectionId] : null;

        if (!sectionHtml && parsedResponseText.sections) {
          const normalizedId = this.sectionId.replace(/^sections--\d+__/, '');
          if (parsedResponseText.sections[normalizedId]) {
            sectionHtml = parsedResponseText.sections[normalizedId];
          } else {
            const keys = Object.keys(parsedResponseText.sections);
            const matchingKey = keys.find((key) => {
              const cleanKey = key.toLowerCase();
              const cleanId = normalizedId.toLowerCase();
              return cleanKey.includes(cleanId) || cleanId.includes(cleanKey) ||
                     (cleanKey.includes('cart') && cleanId.includes('cart')) ||
                     (cleanKey.includes('header') && cleanId.includes('header'));
            });
            if (matchingKey) {
              sectionHtml = parsedResponseText.sections[matchingKey];
            } else if (keys.length === 1) {
              sectionHtml = parsedResponseText.sections[keys[0]];
            }
          }
        }

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: 0,
            source: 'cart-clear-all',
            sections: parsedResponseText.sections,
          })
        );

        if (sectionHtml) {
          morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          const template = document.getElementById('empty-cart-template');
          if (template instanceof HTMLTemplateElement) {
            const clone = document.importNode(template.content, true);
            startViewTransition(() => {
              this.replaceChildren(clone);
            }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
          } else {
            window.location.reload();
          }
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
        clearButton.disabled = false;
        clearButton.removeAttribute('aria-busy');
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = (this.refs.quantitySelectors || [])[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
